import React, {
  useState,
  useEffect,
  useRef,
  cloneElement,
  useMemo,
  useCallback,
} from "react";
import { BaseModal, Button } from "..";
import {
  CloseOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
// Positioning handled manually to match previous behavior (stable, no jitter)

/**
 * Modal component extending the base Modal with enhanced positioning and styling features.
 *
 * @param {Object} props - Component props
 * @param {string|Object|HTMLElement} [props.trigger] - The element or ref to the element to position the modal relative to
 * @param {number|string} [props.borderRadius=16] - Border radius of the modal content
 * @param {boolean|string} [props.fullscreen='auto'] - Whether the modal should take up the full screen ('auto', true, false)
 * @param {boolean} [props.centered] - Whether the modal should be centered (overrides trigger positioning)
 * @param {boolean} [props.open] - Whether the modal is open
 * @param {Object} [props.style] - Style for the modal content
 * @param {Object} [props.bodyStyle] - Style for the modal body
 * @param {number|string} [props.width] - Width of the modal content
 * @param {number|string} [props.height] - Height of the modal content
 * @param {Object} [props.maskStyle] - Style for the modal mask
 * @param {boolean} [props.closable=true] - Whether the modal is closable
 * @param {boolean} [props.fullscreenToggle=false] - Whether to show a toggle button for fullscreen mode
 * @param {number|string} [props.maxHeight='80dvh'] - Maximum height of the modal content
 * @param {boolean} [props.floating=false] - Whether the modal is floating
 * @param {string} [props.backgroundColor='#fff'] - Background color of the modal content
 * @param {string} [props.color='#333'] - Text color of the modal content
 * @param {Array} [props.updatePositionDeps] - Array of dependencies to trigger position recalculation
 * @param {boolean} [props.stickyPositions=false] - Whether the modal should remember its dragged position when reopened
 * @param {Object} [props.header] - Header component of the modal
 * @param {Object} [props.footer] - Footer component of the modal
 * @param {boolean} [props.mask] - Whether the modal should have a mask
 * @param {boolean} [props.allowBackgroundInteraction=false] - Whether to allow interaction with background elements when modal is open
 * @param {boolean} [props.allowBackgroundInteraction=false] - Whether to allow interaction with background elements when modal is open
 * @param {boolean} [props.cursorPositioned=false] - Whether the modal should be positioned at the current cursor location
 * @param {boolean} [props.maskClosableByContextMenu=false] - Whether the modal should close when the mask is right-clicked
 * @param {Object} ...rest - Additional props to pass to the Modal component
 *
 * @see BaseModal.jsx for more details
 */
const Modal = (props) => {
  const {
    trigger,
    borderRadius = 16,
    fullscreen = "auto",
    fullscreenToggle = false,
    centered = false,
    open,
    style,
    bodyStyle,
    width,
    height,
    maskStyle,
    closable = true,
    maxWidth = "80dvw",
    maxHeight = "80dvh",
    floating = false,
    backgroundColor = "#fff",
    color = "#333",
    updatePositionDeps = [],
    stickyPositions = false,
    header,
    footer,
    mask,
    allowBackgroundInteraction = false,
    onCancel,
    onOk,
    minWidth,
    minHeight,
    cursorPositioned = false,
    maskClosableByContextMenu = false,
    ...rest
  } = props;

  const [modalStyle, setModalStyle] = useState({});
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [manualFullScreen, setManualFullScreen] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [resizeTick, setResizeTick] = useState(0);
  const [isPositioned, setIsPositioned] = useState(false);
  const [frozenCursorPos, setFrozenCursorPos] = useState(null);
  const [modalContainer, setModalContainer] = useState(null);
  const originalContainerRef = useRef(null);
  const [uid] = useState(
    `adjustable-modal-${Math.random().toString(36).substr(2, 9)}`,
  );

  const lastMousePos = useRef({ x: 0, y: 0 });
  const internalTriggerRef = useRef(null);
  // (no floating-ui refs needed)

  useEffect(() => {
    const handleMouseMove = (e) => {
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("mousemove", handleMouseMove, true);

    return () => window.removeEventListener("mousemove", handleMouseMove, true);
  }, []);

  // Handle maskClosableByContextMenu globally when modal is open
  useEffect(() => {
    if (open && maskClosableByContextMenu) {
      const handleGlobalContextMenu = (e) => {
        const modalElement = document.querySelector(
          `.${uid} .ant-modal-content`,
        );
        if (modalElement && !modalElement.contains(e.target)) {
          // If the right-click is outside the modal content, close the modal
          onCancel && onCancel();
        }
      };

      // Add with a small delay to prevent the opening click from triggering it
      const timer = setTimeout(() => {
        window.addEventListener("contextmenu", handleGlobalContextMenu, true);
      }, 100);

      return () => {
        clearTimeout(timer);
        window.removeEventListener(
          "contextmenu",
          handleGlobalContextMenu,
          true,
        );
      };
    }
  }, [open, maskClosableByContextMenu, onCancel, uid]);

  const id = props.id || uid;
  const isForcedFullScreen = fullscreen === "auto" && isMobile;
  const isInitialFullScreen = fullscreen === true || isForcedFullScreen;
  const isFullScreen =
    manualFullScreen !== null ? manualFullScreen : isInitialFullScreen;
  const canToggle =
    fullscreenToggle && fullscreen !== false && !isForcedFullScreen;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setResizeTick((prev) => prev + 1);
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {

    if (open) {
      setIsPositioned(false);

      if (cursorPositioned) {
        setFrozenCursorPos({ ...lastMousePos.current });
      } else {
        setFrozenCursorPos(null);
      }

      if (!stickyPositions) {
        setDragOffset({ x: 0, y: 0 });
        setManualFullScreen(null);
      }

      const button =
        trigger && trigger instanceof HTMLElement
          ? trigger
          : internalTriggerRef.current;
      if (button) {
        // Find nearest scrollable parent and ensure button is visible
        let parent = button.parentElement;
        let hasScrollParent = false;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          if (
            /(auto|scroll)/.test(
              style.overflow + style.overflowY + style.overflowX,
            )
          ) {
            hasScrollParent = true;
            break;
          }
          parent = parent.parentElement;
        }

        if (hasScrollParent) {
          button.scrollIntoView({ behavior: "smooth", block: "nearest" });
          // Keep updating position while smooth scrolling
          const timer = setInterval(() => setResizeTick((t) => t + 1), 50);
          setTimeout(() => clearInterval(timer), 800);
        }
        // Determine modal container for mounting when background interaction is allowed
        const scrollParent = hasScrollParent && parent ? parent : document.body;
        if (allowBackgroundInteraction) {
          // If scrollParent isn't positioned, temporarily set it to relative
          try {
            const cs = window.getComputedStyle(scrollParent);
            if (scrollParent !== document.body && cs.position === "static") {
              originalContainerRef.current = scrollParent;
              originalContainerRef.current._vispOldPosition = scrollParent.style.position || "";
              scrollParent.style.position = "relative";
            }
          } catch (err) {
            // ignore
          }
          setModalContainer(scrollParent);
        }
      }
    }
  }, [open, trigger, cursorPositioned, stickyPositions]);

  useEffect(() => {
    if (!allowBackgroundInteraction) {
      if (originalContainerRef.current) {
        try {
          originalContainerRef.current.style.position = originalContainerRef.current._vispOldPosition || "";
          delete originalContainerRef.current._vispOldPosition;
        } catch (err) {}
        originalContainerRef.current = null;
      }
      setModalContainer(null);
    }
  }, [allowBackgroundInteraction]);

  useEffect(() => {
    if (!open) {
      if (originalContainerRef.current) {
        try {
          originalContainerRef.current.style.position = originalContainerRef.current._vispOldPosition || "";
          delete originalContainerRef.current._vispOldPosition;
        } catch (err) {}
        originalContainerRef.current = null;
      }
      setModalContainer(null);
    }
  }, [open]);

  const [wrapStyle, setWrapStyle] = useState(null);

  // Update wrapper style to match the modal container dimensions so
  // the wrap covers the correct area (useful when mounting into
  // a scrollable parent instead of body).
  useEffect(() => {
    let mounted = true;
    const updateWrap = () => {
      if (!modalContainer) {
        if (mounted) setWrapStyle(null);
        return;
      }

      if (modalContainer === document.body) {
        const w = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        const h = Math.max(document.documentElement.scrollHeight || 0, window.innerHeight || 0);
        if (mounted) setWrapStyle({ position: 'absolute', top: 0, left: 0, width: w + 'px', height: h + 'px', pointerEvents: 'none' });
      } else {
        const w = modalContainer.clientWidth;
        const h = Math.max(modalContainer.scrollHeight, modalContainer.clientHeight);
        if (mounted) setWrapStyle({ position: 'absolute', top: 0, left: 0, width: w + 'px', height: h + 'px', pointerEvents: 'none' });
      }
    };

    updateWrap();
    window.addEventListener('resize', updateWrap);
    // If container scrolls or resizes, update occasionally
    try {
      modalContainer && modalContainer.addEventListener && modalContainer.addEventListener('scroll', updateWrap);
    } catch (err) {}

    return () => {
      mounted = false;
      window.removeEventListener('resize', updateWrap);
      try { modalContainer && modalContainer.removeEventListener && modalContainer.removeEventListener('scroll', updateWrap); } catch (e) {}
    };
  }, [modalContainer, open]);

  // Clone the trigger element to attach our ref
  const triggerElement = useMemo(() => {
    if (React.isValidElement(trigger)) {
      return cloneElement(trigger, {
        ref: internalTriggerRef,
      });
    }
    return null;
  }, [trigger]);

  const bodyElement = useMemo(() => {
    if (React.isValidElement(props.children)) {
      return cloneElement(props.children, {
        id: id,
        "data-testid": id,
      });
    }

    return props.children;
  }, [id, props.children]);

  useEffect(() => {
    if (open) {
      const button =
        trigger && trigger instanceof HTMLElement
          ? trigger
          : internalTriggerRef.current;

      if (centered || (!trigger && !frozenCursorPos) || isFullScreen) {
        setIsPositioned(true);
      } else if (button || frozenCursorPos) {
        const rect = frozenCursorPos
          ? {
              top: frozenCursorPos.y,
              bottom: frozenCursorPos.y,
              left: frozenCursorPos.x,
              right: frozenCursorPos.x,
              width: 0,
              height: 0,
            }
          : button.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const [origP1, origP2] = (props.placement || "bottom")
          .replace(/([A-Z])/g, "-$1")
          .toLowerCase()
          .split("-");

        const GAP = cursorPositioned ? 2 : 8;
        const VGAP = cursorPositioned ? 2 : 12;

        // Try to measure actual dimensions if already rendered
        const modalElement = document.querySelector(
          `.${uid} .ant-modal-content`,
        );
        const actualHeight = modalElement?.offsetHeight;
        const actualWidth = modalElement?.offsetWidth;

        // Dimensions estimation
        const getNumeric = (val) => {
          if (typeof val === "number") return val;
          if (typeof val === "string") {
            if (val.includes("px")) return parseFloat(val);
            if (val.includes("vw") || val.includes("dvw"))
              return (parseFloat(val) / 100) * windowWidth;
            if (val.includes("vh") || val.includes("dvh"))
              return (parseFloat(val) / 100) * windowHeight;
            return parseFloat(val);
          }
          return null;
        };

        const nHeight = getNumeric(height);
        const nMaxHeight = getNumeric(maxHeight);
        const estH =
          actualHeight ||
          (nHeight !== null
            ? nHeight
            : cursorPositioned
              ? 250
              : (nMaxHeight ?? windowHeight * 0.8));

        const nWidth = getNumeric(width);
        const nMaxWidth = getNumeric(maxWidth);
        const estW =
          actualWidth ||
          (nWidth !== null
            ? nWidth
            : cursorPositioned
              ? 200
              : (nMaxWidth ?? 400));

        const getOpposite = (p) => {
          const map = {
            top: "bottom",
            bottom: "top",
            left: "right",
            right: "left",
          };
          return map[p] || p;
        };

        // Variants to try
        const variants = [
          [origP1, origP2],
          [getOpposite(origP1), origP2],
          [origP1, getOpposite(origP2)],
          [getOpposite(origP1), getOpposite(origP2)],
        ];

        const evaluateVariant = (p1, p2) => {
          let left = 0,
            top = 0;
          const isVertical = p1 === "top" || p1 === "bottom";

          // Primary axis
          if (p1 === "top") top = rect.top - GAP - estH;
          else if (p1 === "bottom") top = rect.bottom + GAP;
          else if (p1 === "left") left = rect.left - GAP - estW;
          else if (p1 === "right") left = rect.right + GAP;

          // Secondary axis
          if (isVertical) {
            if (p2 === "left") left = rect.left;
            else if (p2 === "right") left = rect.right - estW;
            else left = rect.left + rect.width / 2 - estW / 2;
          } else {
            if (p2 === "top") top = rect.top;
            else if (p2 === "bottom") top = rect.bottom - estH;
            else top = rect.top + rect.height / 2 - estH / 2;
          }

          // Calculate offscreen amount
          const offLeft = Math.max(0, VGAP - left);
          const offRight = Math.max(0, left + estW - (windowWidth - VGAP));
          const offTop = Math.max(0, VGAP - top);
          const offBottom = Math.max(0, top + estH - (windowHeight - VGAP));

          return offLeft + offRight + offTop + offBottom;
        };

        // Find best variant
        let bestVariant = variants[0];
        let minOffscreen = evaluateVariant(...variants[0]);

        for (let i = 1; i < variants.length; i++) {
          const offscreen = evaluateVariant(...variants[i]);
          if (offscreen === 0) {
            bestVariant = variants[i];
            minOffscreen = 0;
            break;
          }
          if (offscreen < minOffscreen) {
            minOffscreen = offscreen;
            bestVariant = variants[i];
          }
        }

        const [p1, p2] = bestVariant;

        // Final positioning based on the best variant
        // We'll compute viewport-based candidates first, then when
        // `allowBackgroundInteraction` is true we convert those to
        // coordinates relative to the chosen container so the modal
        // scrolls with that container.
        const shouldUseDocumentPosition = allowBackgroundInteraction && !isFullScreen;

        // Compute positions in viewport coordinates first
        let viewportTop = null;
        let viewportLeft = null;
        const isVerticalP1 = p1 === "top" || p1 === "bottom";

        if (isVerticalP1) {
          viewportTop = p1 === "top" ? rect.top - GAP - estH : rect.bottom + GAP;
          if (p2 === "left") viewportLeft = rect.left;
          else if (p2 === "right") viewportLeft = rect.right - estW;
          else viewportLeft = rect.left + rect.width / 2 - estW / 2;
        } else {
          viewportLeft = p1 === "left" ? rect.left - GAP - estW : rect.right + GAP;
          if (p2 === "top") viewportTop = rect.top;
          else if (p2 === "bottom") viewportTop = rect.bottom - estH;
          else viewportTop = rect.top + rect.height / 2 - estH / 2;
        }

        // Clamp viewport coordinates so modal stays visible in viewport
        if (typeof viewportTop === "number") {
          viewportTop = Math.max(VGAP, Math.min(viewportTop, windowHeight - estH - VGAP));
        }
        if (typeof viewportLeft === "number") {
          viewportLeft = Math.max(VGAP, Math.min(viewportLeft, windowWidth - estW - VGAP));
        }

        // If we should mount inside a scroll parent, convert viewport coords
        // to container content coords and position absolutely inside that container.
        if (shouldUseDocumentPosition && button) {
          // find nearest scrollable parent (same logic as earlier)
          let p = button.parentElement;
          while (p && p !== document.body) {
            const st = window.getComputedStyle(p);
            if (/(auto|scroll)/.test(st.overflow + st.overflowY + st.overflowX)) break;
            p = p.parentElement;
          }
          const containerEl = p || document.body;
          // ensure modal mounts into this container
          setModalContainer(containerEl);

          const parentRect = containerEl.getBoundingClientRect();
          const parentScrollTop = containerEl === document.body ? (window.pageYOffset || document.documentElement.scrollTop || 0) : containerEl.scrollTop;
          const parentScrollLeft = containerEl === document.body ? (window.pageXOffset || document.documentElement.scrollLeft || 0) : containerEl.scrollLeft;

          const finalTop = typeof viewportTop === "number" ? (viewportTop - parentRect.top + parentScrollTop) : "auto";
          const finalLeft = typeof viewportLeft === "number" ? (viewportLeft - parentRect.left + parentScrollLeft) : "auto";

          const pos = {
            margin: 0,
            position: "absolute",
            padding: 0,
            top: finalTop,
            left: finalLeft,
            bottom: "auto",
            right: "auto",
            transform: "none",
          };

          setModalStyle(pos);
          setIsPositioned(true);
        } else {
          // Use viewport-based positioning (fixed/absolute) as before
          const pos = {
            margin: 0,
            position: cursorPositioned ? "fixed" : "absolute",
            padding: 0,
            top: typeof viewportTop === "number" ? viewportTop : "auto",
            left: typeof viewportLeft === "number" ? viewportLeft : "auto",
            bottom: "auto",
            right: "auto",
            transform: "none",
          };

          setModalStyle(pos);
          setIsPositioned(true);
        }
        
        // After the modal is rendered we may get an improved measurement
        // (actual offsetWidth/offsetHeight). Recompute once to adjust the
        // position if our estimate was off (this matches the original
        // behavior and avoids continuous auto-updates that caused jitter).
        setTimeout(() => {
          try {
            const modalElement2 = document.querySelector(`.${uid} .ant-modal-content`);
            if (!modalElement2) return;

            const actualHeight2 = modalElement2.offsetHeight;
            const actualWidth2 = modalElement2.offsetWidth;

            const finalEstH = actualHeight2 || estH;
            const finalEstW = actualWidth2 || estW;

            let recomputedViewportTop = null;
            let recomputedViewportLeft = null;

            if (isVerticalP1) {
              recomputedViewportTop = p1 === "top" ? rect.top - GAP - finalEstH : rect.bottom + GAP;
              if (p2 === "left") recomputedViewportLeft = rect.left;
              else if (p2 === "right") recomputedViewportLeft = rect.right - finalEstW;
              else recomputedViewportLeft = rect.left + rect.width / 2 - finalEstW / 2;
            } else {
              recomputedViewportLeft = p1 === "left" ? rect.left - GAP - finalEstW : rect.right + GAP;
              if (p2 === "top") recomputedViewportTop = rect.top;
              else if (p2 === "bottom") recomputedViewportTop = rect.bottom - finalEstH;
              else recomputedViewportTop = rect.top + rect.height / 2 - finalEstH / 2;
            }

            if (typeof recomputedViewportTop === "number") {
              recomputedViewportTop = Math.max(VGAP, Math.min(recomputedViewportTop, windowHeight - finalEstH - VGAP));
            }
            if (typeof recomputedViewportLeft === "number") {
              recomputedViewportLeft = Math.max(VGAP, Math.min(recomputedViewportLeft, windowWidth - finalEstW - VGAP));
            }

            if (shouldUseDocumentPosition && button) {
              let p = button.parentElement;
              while (p && p !== document.body) {
                const st = window.getComputedStyle(p);
                if (/(auto|scroll)/.test(st.overflow + st.overflowY + st.overflowX)) break;
                p = p.parentElement;
              }
              const containerEl = p || document.body;
              // ensure modal mounts into this container
              setModalContainer(containerEl);

              const parentRect = containerEl.getBoundingClientRect();
              const parentScrollTop = containerEl === document.body ? (window.pageYOffset || document.documentElement.scrollTop || 0) : containerEl.scrollTop;
              const parentScrollLeft = containerEl === document.body ? (window.pageXOffset || document.documentElement.scrollLeft || 0) : containerEl.scrollLeft;

              const finalTop = typeof recomputedViewportTop === "number" ? (recomputedViewportTop - parentRect.top + parentScrollTop) : "auto";
              const finalLeft = typeof recomputedViewportLeft === "number" ? (recomputedViewportLeft - parentRect.left + parentScrollLeft) : "auto";

              const pos2 = {
                margin: 0,
                position: "absolute",
                padding: 0,
                top: finalTop,
                left: finalLeft,
                bottom: "auto",
                right: "auto",
                transform: "none",
              };

              setModalStyle(pos2);
            } else {
              const pos2 = {
                margin: 0,
                position: cursorPositioned ? "fixed" : "absolute",
                padding: 0,
                top: typeof recomputedViewportTop === "number" ? recomputedViewportTop : "auto",
                left: typeof recomputedViewportLeft === "number" ? recomputedViewportLeft : "auto",
                bottom: "auto",
                right: "auto",
                transform: "none",
              };

              setModalStyle(pos2);
            }
          } catch (err) {
            // ignore measurement errors
          }
        }, 0);
      }
    }

    return () => {};
  }, [
    open,
    centered,
    trigger,
    props.placement,
    resizeTick,
    isFullScreen,
    width,
    height,
    maxWidth,
    maxHeight,
    frozenCursorPos,
    allowBackgroundInteraction,
    ...updatePositionDeps,
  ]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!floating || isFullScreen) return;

      const modalElement = document.querySelector(`.${uid} .ant-modal`);
      if (!modalElement) return;

      const rect = modalElement.getBoundingClientRect();
      const containerEl = modalContainer || document.body;
      const parentRect = containerEl.getBoundingClientRect();
      const parentScrollTop = containerEl === document.body ? (window.pageYOffset || document.documentElement.scrollTop || 0) : containerEl.scrollTop;
      const parentScrollLeft = containerEl === document.body ? (window.pageXOffset || document.documentElement.scrollLeft || 0) : containerEl.scrollLeft;

      // Determine starting top/left in container coordinates
      const baseTop = typeof modalStyle.top === 'number' ? modalStyle.top : (rect.top - parentRect.top + parentScrollTop);
      const baseLeft = typeof modalStyle.left === 'number' ? modalStyle.left : (rect.left - parentRect.left + parentScrollLeft);

      const startX = e.clientX;
      const startY = e.clientY;

      const handleMouseMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        let newLeft = baseLeft + deltaX;
        let newTop = baseTop + deltaY;

        // Clamp so modal's visible rect stays inside viewport (VGAP margin)
        const VGAP = 12;
        const minLeft = VGAP - parentRect.left + parentScrollLeft;
        const maxLeft = window.innerWidth - VGAP - rect.width - parentRect.left + parentScrollLeft;
        const minTop = VGAP - parentRect.top + parentScrollTop;
        const maxTop = window.innerHeight - VGAP - rect.height - parentRect.top + parentScrollTop;

        newLeft = Math.max(minLeft, Math.min(maxLeft, newLeft));
        newTop = Math.max(minTop, Math.min(maxTop, newTop));

        setModalStyle((prev) => ({ ...prev, top: newTop, left: newLeft, position: 'absolute' }));
      };

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [floating, isFullScreen, uid, modalContainer, modalStyle],
  );

  const modalHeader = useMemo(
    () => (
      <div
        data-testid={`${id}__header-container`}
        onMouseDown={handleMouseDown}
        style={{
          cursor: floating && !isFullScreen ? "move" : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          userSelect: "none",
          gap: "8px",
          paddingLeft: 4,
        }}
      >
        <div
          data-testid={`${id}__header`}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "1rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {header}
        </div>
        {(canToggle || closable) && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {canToggle && (
              <Button
                shape="circle"
                data-testid={`${id}__fullscreen-toggle-button`}
                style={{
                  fontSize: isFullScreen ? "14px" : "18px",
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                }}
                icon={
                  isFullScreen ? (
                    <FullscreenExitOutlined style={{ lineHeight: "unset" }} />
                  ) : (
                    <FullscreenOutlined style={{ lineHeight: "unset" }} />
                  )
                }
                onClick={() => setManualFullScreen(!isFullScreen)}
              />
            )}
            {closable && (
              <Button
                shape="circle"
                data-testid={`${id}__close-button`}
                onClick={onCancel}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                }}
                icon={<CloseOutlined style={{ lineHeight: "unset" }} />}
              />
            )}
          </div>
        )}
      </div>
    ),
    [
      handleMouseDown,
      floating,
      isFullScreen,
      header,
      closable,
      canToggle,
      onCancel,
    ],
  );

  const modalRender = useCallback(
    (modal) => {
      if (React.isValidElement(modal)) {
        return cloneElement(modal, {
          id: id,
          "data-testid": id,
        });
      }

      return modal;
    },
    [id],
  );

  // Merge calculated styles with passed styles
  const finalStyle = useMemo(
    () => ({
      ...modalStyle,
      ...style,
      ...(isFullScreen
        ? {
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            margin: 0,
            maxWidth: "100%",
          }
        : {
            transform:
              [
                modalStyle.transform && modalStyle.transform !== "none"
                  ? modalStyle.transform
                  : "",
                dragOffset.x || dragOffset.y
                  ? `translate(${dragOffset.x}px, ${dragOffset.y}px)`
                  : "",
              ]
                .filter(Boolean)
                .join(" ") || undefined,
          }),
    }),
    [modalStyle, style, isFullScreen, dragOffset],
  );

  const finalBodyStyle = useMemo(
    () => ({
      ...{ padding: 16 },
      ...bodyStyle,
      ...(isFullScreen ? { width: "100dvw" } : {}),
    }),
    [bodyStyle, isFullScreen],
  );

  const finalMaskStyle = useMemo(() => {
    const res = { ...maskStyle };
    if ((centered === false && mask !== true) || mask === false || allowBackgroundInteraction === true) {
      res.backgroundColor = "transparent";
    } else {
      res.zIndex = 1030;
    }

    return res;
  }, [centered, mask, maskStyle, allowBackgroundInteraction]);

  const dynamicStyleContent = useMemo(
    () => `
                    .${uid} {
                      z-index: 1030;
                      ${allowBackgroundInteraction && !isFullScreen ? "position: absolute !important; top: 0; left: 0; width: 100%; height: auto; pointer-events: none !important;" : ""}
                    }
                    .${uid} .ant-modal {
                      ${allowBackgroundInteraction && !isFullScreen ? "position: absolute !important; pointer-events: auto !important;" : ""}
                        ${
                          isFullScreen
                            ? `
                            top: 0 !important;
                            left: 0 !important;
                            right: 0 !important;
                            bottom: 0 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            max-width: 100% !important;
                            width: 100% !important;
                            height: 100% !important;
                            transform: none !important;
                            padding-bottom: 0 !important;
                        `
                            : isPositioned
                              ? `
                            top: ${finalStyle.top !== undefined && finalStyle.top !== "auto" ? (typeof finalStyle.top === "number" ? finalStyle.top + "px" : finalStyle.top) : "auto"} !important;
                            bottom: ${finalStyle.bottom !== undefined && finalStyle.bottom !== "auto" ? (typeof finalStyle.bottom === "number" ? finalStyle.bottom + "px" : finalStyle.bottom) : "auto"} !important;
                            left: ${finalStyle.left !== undefined && finalStyle.left !== "auto" ? (typeof finalStyle.left === "number" ? finalStyle.left + "px" : finalStyle.left) : "auto"} !important;
                            right: ${finalStyle.right !== undefined && finalStyle.right !== "auto" ? (typeof finalStyle.right === "number" ? finalStyle.right + "px" : finalStyle.right) : "auto"} !important;
                            transform: ${finalStyle.transform || "none"} !important;
                            margin: ${finalStyle.margin || 0} !important;
                            position: ${finalStyle.position || "relative"} !important;
                            padding: ${finalStyle.padding || 0} !important;
                            ${width ? `width: ${typeof width === "number" ? width + "px" : width} !important;` : "width: auto !important;"}
                            max-width: ${typeof maxWidth === "number" ? maxWidth + "px" : maxWidth} !important;
                        `
                              : ""
                        }
                    }
                    @keyframes modal-fade-in {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    .${uid} .ant-modal-content {
                        animation: modal-fade-in 0.4s ease-out;
                        background-color: ${backgroundColor};
                        color: ${color};
                        border-radius: ${isFullScreen ? 0 : borderRadius}${typeof borderRadius === "number" ? "px" : ""} !important;
                        box-shadow: ${isFullScreen ? "none" : "rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;"} !important;
                        ${
                          isFullScreen
                            ? "height: 100dvh; width: 100dvw; display: flex !important; flex-direction: column !important; overflow: hidden !important;"
                            : `
                            ${height ? `height: ${typeof height === "number" ? height + "px" : height} !important;` : ""}
                            ${maxHeight ? `max-height: ${typeof maxHeight === "number" ? maxHeight + "px" : maxHeight} !important;` : ""}
                            ${minHeight ? `min-height: ${typeof minHeight === "number" ? minHeight + "px" : minHeight} !important;` : ""}
                            ${width ? `width: ${typeof width === "number" ? width + "px" : width} !important;` : ""}
                            ${maxWidth ? `max-width: ${typeof maxWidth === "number" ? maxWidth + "px" : maxWidth} !important;` : ""}
                            ${minWidth ? `min-width: ${typeof minWidth === "number" ? minWidth + "px" : minWidth} !important;` : ""}
                            display: flex !important;
                            flex-direction: column !important;
                            overflow: hidden !important;
                        `
                        }
                    }
                    .${uid} .ant-modal-header {
                        padding: 8px;
                        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                        background: transparent;
                        color: ${color};
                        border-radius: ${isFullScreen ? 0 : borderRadius}${typeof borderRadius === "number" ? "px" : ""} ${isFullScreen ? 0 : borderRadius}${typeof borderRadius === "number" ? "px" : ""} 0 0 !important;
                    }
                    .${uid} .ant-modal-header .ant-modal-title {
                        font-weight: 600;
                        font-size: 16px;
                        color: inherit;
                    }
                    .${uid} .ant-modal-body {
                        flex: 1;
                        min-height: 0;
                        overflow-x: hidden;
                        display: flex;
                        flex-direction: column;
                    }
                    .${uid} .ant-modal-footer {
                        border-top: 1px solid rgba(0, 0, 0, 0.05);
                        background: transparent;
                        color: ${color};
                        border-radius: 0 0 ${isFullScreen ? 0 : borderRadius}${typeof borderRadius === "number" ? "px" : ""} ${isFullScreen ? 0 : borderRadius}${typeof borderRadius === "number" ? "px" : ""} !important;
                    }
                    .visp-modal-trigger-highlight {
                        position: fixed !important;
                        z-index: 1001 !important;
                    }
                    .${uid} .ant-modal *::-webkit-scrollbar {
                      background-color: transparent !important;
                    }
                `,
    [
      uid,
      isFullScreen,
      isPositioned,
      finalStyle,
      backgroundColor,
      color,
      borderRadius,
      height,
      maxHeight,
      minHeight,
      width,
      maxWidth,
      minWidth,
      allowBackgroundInteraction,
    ],
  );

  return (
    <React.Fragment>
      {triggerElement}
      <BaseModal
        {...rest}
        {...(modalContainer ? { getContainer: () => modalContainer } : {})}
        destroyOnClose={true}
        afterClose={() => {
          // cleanup container modifications and wrapper styles after modal fully closes
          try {
            if (originalContainerRef.current) {
              originalContainerRef.current.style.position = originalContainerRef.current._vispOldPosition || "";
              delete originalContainerRef.current._vispOldPosition;
              originalContainerRef.current = null;
            }
          } catch (err) {}
          setModalContainer(null);
          setWrapStyle(null);
          setModalStyle({});
          setIsPositioned(false);
        }}
        open={open && isPositioned}
        centered={centered}
        width={isFullScreen ? "100%" : width}
        style={isPositioned ? finalStyle : {}}
        maskStyle={finalMaskStyle}
        bodyStyle={finalBodyStyle}
        wrapClassName={`${uid} ${props.wrapClassName || ""}`}
        focusTriggerAfterClose={false}
        closable={false}
        title={!header && !closable ? null : modalHeader}
        footer={footer}
        mask={allowBackgroundInteraction ? false : mask}
        onCancel={onCancel}
        onOk={onOk}
        modalRender={modalRender}
        transitionName=""
        wrapProps={{
          onContextMenu: (e) => {
            const target = e.target;
            if (!(target instanceof Element)) return;
            if (target.closest(".ant-modal-content")) return;
            if (!maskClosableByContextMenu) return;
            e.preventDefault();
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              onCancel && onCancel(e);
            }
          },
          style: wrapStyle || undefined,
        }}
      >
        {bodyElement}
      </BaseModal>
      <style>{dynamicStyleContent}</style>
    </React.Fragment>
  );
};

// Preserve Modal static methods
Modal.info = BaseModal.info;
Modal.success = BaseModal.success;
Modal.error = BaseModal.error;
Modal.warning = BaseModal.warning;
Modal.confirm = BaseModal.confirm;
Modal.destroyAll = BaseModal.destroyAll;

export default Modal;
